/**
 * User Permission Summary Component
 *
 * Displays overview cards with user details and permission statistics
 */

import React from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Stack,
  LinearProgress,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Alert,
  AlertTitle
} from '@mui/material';
import {
  Person,
  Group,
  Business,
  Security,
  CheckCircle,
  Cancel,
  Warning,
  LocationOn,
  AccessTime,
  Assignment
} from '@mui/icons-material';

export default function UserPermissionSummary({
  user,
  permissions,
  grantCount,
  securityVersion,
  grants = []
}) {
  // Calculate permission statistics
  const calculateStats = () => {
    if (!permissions?.navigation) {
      return {
        totalResources: 0,
        allowedActions: 0,
        deniedActions: 0,
        totalActions: 0
      };
    }

    let totalResources = 0;
    let allowedActions = 0;
    let deniedActions = 0;
    let totalActions = 0;

    Object.entries(permissions.navigation).forEach(([resource, actions]) => {
      totalResources++;
      Object.entries(actions).forEach(([action, permission]) => {
        totalActions++;
        if (permission === 'ALLOW') {
          allowedActions++;
        } else if (permission === 'DENY') {
          deniedActions++;
        }
      });
    });

    return {
      totalResources,
      allowedActions,
      deniedActions,
      totalActions
    };
  };

  const stats = calculateStats();
  const allowPercentage = stats.totalActions > 0
    ? Math.round((stats.allowedActions / stats.totalActions) * 100)
    : 0;

  // Calculate data scope summary
  const dataScope = permissions?.dataScope || {};
  const { scopeType, accessibleLocations = [], accessibleOrgUnits = [] } = dataScope;
  const dataScopeCount = accessibleLocations.length + accessibleOrgUnits.length;
  const hasFullAccess = scopeType === 'UNIVERSAL';

  const getUserStatusColor = () => {
    if (user?.status === 'ACTIVE') return 'success';
    if (user?.status === 'INACTIVE') return 'default';
    if (user?.status === 'SUSPENDED') return 'error';
    return 'default';
  };

  return (
    <Box>
      <Grid container spacing={3}>
        {/* User Information Card */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="flex-start" mb={2}>
                <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
                  <Person />
                </Avatar>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" gutterBottom>
                    User Information
                  </Typography>
                  <Chip
                    label={user?.status || 'Unknown'}
                    size="small"
                    color={getUserStatusColor()}
                    variant="outlined"
                  />
                </Box>
              </Stack>

              <Divider sx={{ my: 2 }} />

              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <Person />
                  </ListItemIcon>
                  <ListItemText
                    primary="Username"
                    secondary={user?.userName || user?.name || 'Unknown'}
                  />
                </ListItem>

                <ListItem>
                  <ListItemIcon>
                    <Group />
                  </ListItemIcon>
                  <ListItemText
                    primary="Group / Role"
                    secondary={`${user?.group || 'None'} / ${user?.groupRole || 'None'}`}
                  />
                </ListItem>

                <ListItem>
                  <ListItemIcon>
                    <Business />
                  </ListItemIcon>
                  <ListItemText
                    primary="Group / Role"
                    secondary={user?.group && user?.groupRole ? `${user.group} / ${user.groupRole}` : 'Not Set'}
                  />
                </ListItem>

                <ListItem>
                  <ListItemIcon>
                    <LocationOn />
                  </ListItemIcon>
                  <ListItemText
                    primary="Primary Location"
                    secondary={user?.primaryLocation || 'Not Assigned'}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Permission Statistics Card */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="flex-start" mb={2}>
                <Avatar sx={{ bgcolor: 'success.main', width: 56, height: 56 }}>
                  <Security />
                </Avatar>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" gutterBottom>
                    Permission Statistics
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {stats.totalResources} Resources × {stats.totalActions} Actions
                  </Typography>
                </Box>
              </Stack>

              <Box sx={{ my: 3 }}>
                <Stack direction="row" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">Permission Coverage</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {allowPercentage}%
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={allowPercentage}
                  sx={{ height: 8, borderRadius: 1 }}
                  color={allowPercentage > 75 ? 'success' : allowPercentage > 50 ? 'warning' : 'error'}
                />
              </Box>

              <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={6}>
                  <Box textAlign="center">
                    <CheckCircle sx={{ color: 'success.main', fontSize: 32 }} />
                    <Typography variant="h6">{stats.allowedActions}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Allowed
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box textAlign="center">
                    <Cancel sx={{ color: 'error.main', fontSize: 32 }} />
                    <Typography variant="h6">{stats.deniedActions}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Denied
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Grant Summary Card */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                <Assignment sx={{ color: 'primary.main', fontSize: 32 }} />
                <Box>
                  <Typography variant="h6">Grant Summary</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {grantCount === 0
                      ? 'No grants applicable to this user'
                      : grantCount === 1
                      ? 'Permissions derived from 1 grant'
                      : `Permissions derived from ${grantCount} grants`}
                  </Typography>
                </Box>
              </Stack>

              <Divider sx={{ my: 2 }} />

              {grantCount === 0 ? (
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    No grants are currently applicable to this user
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={1.5}>
                  {grants.map((grant, index) => (
                    <Stack key={grant.id || index} direction="row" spacing={1} alignItems="center">
                      {grant.targetType === 'USER' ? (
                        <>
                          <Person fontSize="small" color="action" />
                          <Typography variant="body2">User Grant</Typography>
                        </>
                      ) : (
                        <>
                          <Group fontSize="small" color="action" />
                          <Typography variant="body2">
                            Role Grant: {grant.subjectValue || 'Unknown Role'}
                          </Typography>
                        </>
                      )}
                      <Chip
                        label={`Priority ${grant.priority || 100}`}
                        size="small"
                        color={grant.priority > 500 ? 'error' : 'default'}
                        variant="outlined"
                      />
                    </Stack>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Data Scope Card */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                <LocationOn sx={{ color: 'info.main', fontSize: 32 }} />
                <Box>
                  <Typography variant="h6">Data Scope</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Accessible Locations and OrgUnits
                  </Typography>
                </Box>
              </Stack>

              <Divider sx={{ my: 2 }} />

              {hasFullAccess ? (
                <Alert severity="warning" icon={<Warning />}>
                  <AlertTitle sx={{ fontSize: '0.875rem' }}>Full Access</AlertTitle>
                  This user has access to all Locations and OrgUnits
                </Alert>
              ) : (
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Accessible Locations
                    </Typography>
                    <Typography variant="h4">{accessibleLocations.length}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Accessible OrgUnits
                    </Typography>
                    <Typography variant="h4">{accessibleOrgUnits.length}</Typography>
                  </Grid>
                </Grid>
              )}

              {(accessibleLocations.length > 0 || accessibleOrgUnits.length > 0) && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Sample:
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {accessibleLocations.slice(0, 3).map(location => (
                      <Chip
                        key={location}
                        label={location}
                        size="small"
                        variant="outlined"
                        icon={<LocationOn />}
                      />
                    ))}
                    {accessibleOrgUnits.slice(0, 2).map(orgUnit => (
                      <Chip
                        key={orgUnit}
                        label={orgUnit}
                        size="small"
                        variant="outlined"
                        icon={<Business />}
                      />
                    ))}
                    {(accessibleLocations.length + accessibleOrgUnits.length) > 5 && (
                      <Chip
                        label={`+${(accessibleLocations.length + accessibleOrgUnits.length) - 5} more`}
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Stack>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Calculation Info Card */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Stack direction="row" spacing={3} alignItems="flex-start" flexWrap="wrap">
                <AccessTime sx={{ color: 'text.secondary', mt: 0.5 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    Last Calculated
                  </Typography>
                  <Typography variant="body2">
                    {permissions?.calculatedAt
                      ? new Date(permissions.calculatedAt).toLocaleString()
                      : 'Never calculated'}
                  </Typography>
                </Box>
                <Divider orientation="vertical" flexItem />
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    Security Version
                  </Typography>
                  <Typography variant="body2">
                    v{securityVersion || 'N/A'}
                  </Typography>
                </Box>
                <Divider orientation="vertical" flexItem />
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    Cache Status
                  </Typography>
                  <Chip
                    label={permissions?.isStale ? 'Stale' : 'Valid'}
                    size="small"
                    color={permissions?.isStale ? 'warning' : 'success'}
                    variant="outlined"
                  />
                </Box>
                <Divider orientation="vertical" flexItem />
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    Calculation Time
                  </Typography>
                  <Typography variant="body2">
                    {permissions?.calculationTime
                      ? `${permissions.calculationTime}ms`
                      : 'N/A'}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}